// src/components/Auth/SignIn.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { getHomePath } from '../../utils/permissions';
import './Auth.css';

const SignIn = ({ onLogin }) => {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  const [error, setError] = useState(() => {
    try {
      return sessionStorage.getItem('session_message') || '';
    } catch (e) {
      return '';
    }
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      sessionStorage.removeItem('session_message');
    } catch (e) {
      // ignore
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Remove old error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError('');
    setLoading(true);

    const API_URL =
      process.env.REACT_APP_API_URL ||
      'https://super-backend.hira-tech.co.uk/user';

    const email = formData.email.trim();
    const password = formData.password;

    if (!email || !password) {
      setError('Please enter your email and password.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      let data = {};

      try {
        data = await response.json();
      } catch (jsonError) {
        data = {};
      }

      console.log('LOGIN RESPONSE:', response.status, data);

      if (!response.ok) {
        const backendError =
          data?.errors?.non_field_errors?.[0] ||
          data?.error ||
          data?.detail ||
          'Invalid email or password';

        setError(backendError);
        setLoading(false);
        return;
      }

      if (!data?.token?.access) {
        setError('Login failed. Access token was not returned by the server.');
        setLoading(false);
        return;
      }

      const apiUser = data.token.user;

      if (!apiUser) {
        setError('Login failed. User information was not returned.');
        setLoading(false);
        return;
      }

      // The account type comes from the server, so the system
      // recognises SUPER_ADMIN / STAFF by itself from the email.
      const accountType = String(apiUser.type || '').toUpperCase();

      // ==========================================================
      // ONLY SUPER_ADMIN AND STAFF CAN LOGIN
      // ==========================================================

      if (!['SUPER_ADMIN', 'STAFF'].includes(accountType)) {
        setError(
          'This account is not allowed to access the Super Admin panel.'
        );
        setLoading(false);
        return;
      }

      // ==========================================================
      // STAFF MUST HAVE A SHOP
      // ==========================================================

      if (accountType === 'STAFF' && !apiUser.shop_id) {
        setError(
          'No shop is assigned to this Staff account. Please contact the Super Admin.'
        );

        setLoading(false);
        return;
      }

      // ==========================================================
      // CLEAR OLD LOGIN DATA
      // ==========================================================

      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('jwt');
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');

      // ==========================================================
      // SAVE TOKENS
      // ==========================================================

      localStorage.setItem(
        'access_token',
        data.token.access
      );

      localStorage.setItem(
        'refresh_token',
        data.token.refresh
      );

      // ==========================================================
      // USER DATA
      // ==========================================================

      const userData = {
        id: apiUser.id,

        first_Name: apiUser.first_Name || '',
        last_Name: apiUser.last_Name || '',

        email: apiUser.email || '',

        type: accountType,

        allowed_pages: Array.isArray(apiUser.allowed_pages)
          ? apiUser.allowed_pages
          : [],

        is_super_admin:
          accountType === 'SUPER_ADMIN',

        shop_id:
          apiUser.shop_id || null,

        shop_name:
          apiUser.shop_name || null,

        shop_subdomain:
          apiUser.shop_subdomain || null,
      };

      localStorage.setItem(
        'user',
        JSON.stringify(userData)
      );

      // ==========================================================
      // STAFF SHOP LOCK
      // ==========================================================

      if (accountType === 'STAFF') {
        localStorage.setItem(
          'selectedShopId',
          String(userData.shop_id)
        );

        localStorage.setItem(
          'selected_shop_id',
          String(userData.shop_id)
        );

        localStorage.setItem(
          'selectedShopName',
          userData.shop_name || 'My Shop'
        );
      }

      // ==========================================================
      // AUTHENTICATED
      // ==========================================================

      localStorage.setItem(
        'isAuthenticated',
        'true'
      );

      // ==========================================================
      // OPTIONAL REMEMBER EMAIL
      // ==========================================================

      if (formData.rememberMe) {
        localStorage.setItem(
          'rememberedEmail',
          email
        );
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      // ==========================================================
      // APP LOGIN CALLBACK
      // ==========================================================

      if (typeof onLogin === 'function') {
        onLogin();
      }

      // ==========================================================
      // REDIRECT
      // ==========================================================

      const homePath = getHomePath(userData);

      navigate(homePath, {
        replace: true,
      });
    } catch (err) {
      console.error('Login error:', err);

      setError(
        'Network error. Please check your connection and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">

        {/* =====================================================
            HEADER
        ====================================================== */}

        <div className="auth-header">

          <div className="logo-container">
            <img
              src="/image.png"
              alt="HIRA-TECH Logo"
              className="auth-logo"
            />
          </div>

          <h2>Welcome Back</h2>

          <p>
            Sign in to your account to continue
          </p>

        </div>

        {/* =====================================================
            ERROR
        ====================================================== */}

        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}

        {/* =====================================================
            FORM
        ====================================================== */}

        <form
          onSubmit={handleSubmit}
          className="auth-form"
        >

                    {/* EMAIL */}
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <div className="input-wrapper">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                autoComplete="email"
                required
              />
            </div>
          </div>

          {/* PASSWORD */}
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <div className="input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* REMEMBER ME */}
          <div className="form-options" style={{ marginBottom: '24px' }}>
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
                disabled={loading}
              />
              <span>Remember me</span>
            </label>
          </div>

          {/* ===================================================
              SUBMIT
          ==================================================== */}

          <button
            type="submit"
            className="auth-btn"
            disabled={loading}
          >
            {loading
              ? 'Signing in...'
              : 'Sign In'}
          </button>

        </form>

      </div>
    </div>
  );
};

export default SignIn;